import {CommentIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Banner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {CommentsFilter, type CommentsFilterState, getDefaultFilterState, useFilteredThreadIds} from './CommentsFilter'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import type {Author} from '@github-ui/diff-lines/conversation-types'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PageLimits} from '../../page-data/payloads/files'
import {ZeroState} from './ZeroState'
import {useInfiniteThreadPreviewsPageData} from '../../page-data/loaders/use-thread-previews'
import type {ThreadPreviewsCompPayload} from '../../page-data/payloads/thread-previews'
import {isThreadPaginationEnabled} from '../PageLimitsBanner'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits: PageLimits
  pathName: string
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
  setThreadPreviews: (threadPreviews: ThreadPreviewsCompPayload) => void
}

/**
 * Content component that fetches and renders thread previews.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function CommentsSidePanelContent({
  filteredThreadIds,
  onClose,
  pathName,
  isOpen,
  setThreadPreviews,
  tabSize,
}: Omit<CommentsSidePanelProps, 'pageLimits'>) {
  const {
    data,
    hasNextPage,
    isFetchingNextPage: isFetchingThreadPreviews,
    fetchNextPage,
  } = useInfiniteThreadPreviewsPageData({
    pathName,
  })

  const threadPreviews = useMemo(() => data.pages.flatMap(page => page.data), [data])

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingThreadPreviews && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingThreadPreviews, fetchNextPage])

  useEffect(() => {
    setThreadPreviews(threadPreviews)
  }, [threadPreviews, setThreadPreviews])

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const handleNavigateToDiffComment = useCallback(() => {
    onClose()
  }, [onClose])

  const threadSummaries = threadPreviews
    .map(threadPreview => {
      if (!threadPreview || !filteredThreadIds.has(threadPreview.previewInfo.threadId)) return null
      return (
        <ThreadPreview
          key={threadPreview.previewInfo.threadId}
          tabSize={tabSize}
          threadPreview={threadPreview}
          onNavigateToDiffComment={handleNavigateToDiffComment}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isFetchingThreadPreviews) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isFetchingThreadPreviews, debouncedAnnounce, threadSummaries])

  return (
    <>
      {threadSummaries.length > 0 ? (
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            {threadSummaries}
            {isFetchingThreadPreviews && <ThreadPreviewSkeleton count={1} />}
          </div>
        </Suspense>
      ) : isFetchingThreadPreviews ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement | null>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {pathName, toggleSidesheetRef, pageLimits, ...rest} = props
  const [threadPreviews, setThreadPreviews] = useState<ThreadPreviewsCompPayload>([])
  const [filterState, setFilterState] = useState<CommentsFilterState>(() => getDefaultFilterState())
  const filteredThreadIds = useFilteredThreadIds(threadPreviews, filterState)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const authorList = useMemo(() => {
    const authors: Author[] = []

    for (const thread of threadPreviews) {
      const firstComment = thread.previewInfo.firstComment
      if (firstComment?.author && !authors.some(author => author.login === firstComment.author?.login)) {
        // Copilot goes to the top of the filter
        if (firstComment.author.login === 'Copilot') {
          authors.unshift(firstComment.author)
        } else {
          authors.push(firstComment.author)
        }
      }
    }

    return authors
  }, [threadPreviews])

  const threadPaginationEnabled = isThreadPaginationEnabled(pageLimits)
  const limitExceeded = threadPaginationEnabled
    ? pageLimits.reviewThreadsPaginatedLimitExceeded
    : pageLimits.reviewThreadsLimitExceeded
  const limitCount = threadPaginationEnabled ? pageLimits.reviewThreadsPaginatedLimit : pageLimits.reviewThreadsLimit

  if (!rest.isOpen) return null
  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={rest.onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={rest.onClose}
            />
          </div>
          <CommentsFilter
            authorList={authorList}
            className="mt-2 width-full"
            filterState={filterState}
            onFilterStateChange={setFilterState}
          />
        </Dialog.Header>
      )}
    >
      {limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="mb-3"
        />
      )}
      <Suspense fallback={<ThreadPreviewSkeleton />}>
        <CommentsSidePanelContent
          {...rest}
          filteredThreadIds={filteredThreadIds}
          pathName={pathName}
          setThreadPreviews={setThreadPreviews}
        />
      </Suspense>
    </Dialog>
  )
})

try{ CommentsSidePanelContent.displayName ||= 'CommentsSidePanelContent' } catch {}
try{ CommentsSidePanel.displayName ||= 'CommentsSidePanel' } catch {}